import { Minus, Plus, ShoppingCart } from '../ui/icons';
import { useCart } from '../../hooks/useCart';
import { useI18n } from '../../hooks/useI18n';
import type { Product } from '../../types';

interface ProductQuantityControlProps {
  product: Product;
  className?: string;
  /** Chat cards are ~150px wide, too narrow for the full minus/plus stepper.
   *  This renders just the add button with a quantity badge when already in cart. */
  compact?: boolean;
}

export default function ProductQuantityControl({ product, className = '', compact = false }: ProductQuantityControlProps) {
  const { addToCart, items, updateQuantity } = useCart();
  const { t } = useI18n();
  const quantity = items.find((item) => item.productId === product.id)?.quantity ?? 0;

  if (compact) {
    return (
      <button
        data-testid="add-to-cart"
        onClick={() => addToCart(product)}
        className={`flex w-full items-center justify-center gap-1.5 rounded-lg bg-success-strong py-1.5 text-xs font-semibold text-success-contrast shadow-sm shadow-success/30 transition-colors hover:bg-success ${className}`}
        aria-label={t('detail_add_cart')}
      >
        <Plus className="h-3.5 w-3.5" />
        <span>{quantity > 0 ? `${t('chat_in_cart')} (${quantity})` : t('detail_add_cart')}</span>
      </button>
    );
  }

  return (
    <div className={`flex items-center justify-between rounded-xl border border-glass-line/15 bg-raised/60 p-1.5 backdrop-blur-md ${className}`}>
      <button 
        onClick={() => quantity > 0 && updateQuantity(product.id, quantity - 1)} 
        disabled={quantity === 0}
        className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${quantity === 0 ? 'text-muted/30 cursor-not-allowed' : 'text-muted hover:bg-danger/15 hover:text-danger'}`} 
        aria-label="Restar una unidad"
      >
        <Minus className="h-4 w-4" />
      </button>
      <span className="flex items-center gap-2 px-2 text-sm font-medium text-ink/85"><ShoppingCart className="h-4 w-4 text-success" />{quantity}</span>
      <button data-testid="add-to-cart" onClick={() => addToCart(product)} className={`flex h-9 w-9 items-center justify-center rounded-lg bg-success-strong text-success-contrast shadow-sm shadow-success/30 transition-colors hover:bg-success`} aria-label={t('detail_add_cart')}>
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}
